import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { useAuth } from "@/lib/auth-context";
import { api, formatUSD } from "@/lib/api";
import { ArrowUpRight, Plus, Coins, FolderOpen, Clock, House, Star, Receipt, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ReferralCard } from "@/components/ReferralCard";

const STATUS_LABEL = {
  submitted: "Submitted",
  preproduction: "Preproduction",
  design: "Design",
  review: "Review",
  final: "Final",
  delivered: "Delivered",
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [projects, setProjects] = useState([]);
  const [charges, setCharges] = useState([]);
  const [reviewEligible, setReviewEligible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewalSaving, setRenewalSaving] = useState(false);

  const reloadCharges = async () => {
    try {
      const { data } = await api.get("/charges?status=pending");
      setCharges(data);
    } catch (_) {}
  };

  const loadEligible = async () => {
    try {
      const { data } = await api.get("/reviews/me/eligible");
      setReviewEligible(data);
    } catch (_) {}
  };

  useEffect(() => {
    (async () => {
      try {
        const [p] = await Promise.all([api.get("/projects"), reloadCharges(), loadEligible()]);
        setProjects(p.data);
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  const showReviewCta = reviewEligible && (
    reviewEligible.eligible_projects.length > 0 || reviewEligible.can_leave_general
  );

  const open = projects.filter((p) => p.status !== "delivered");
  const balanceDue = projects.reduce(
    (sum, p) => sum + Math.max(0, (p.total_usd || 0) - (p.paid_amount || 0) - (p.payment_method === "points" ? p.total_usd : 0)),
    0
  );

  const totalChargesDue = charges.reduce((s, c) => s + (c.amount || 0), 0);

  const setRenewalMode = async (mode) => {
    setRenewalSaving(true);
    try {
      await api.patch("/auth/maintenance-renewal-mode", { renewal_mode: mode });
      await refresh();
      toast.success(`Renewal set to ${mode}`);
    } catch (e) {
      toast.error("Could not update renewal preference");
    } finally {
      setRenewalSaving(false);
    }
  };

  // Days remaining until maintenance plan expires
  let maintenanceDaysLeft = null;
  if (user?.has_maintenance_plan && user?.maintenance_expires_at) {
    try {
      const exp = new Date(user.maintenance_expires_at);
      maintenanceDaysLeft = Math.max(0, Math.ceil((exp - Date.now()) / 86400000));
    } catch (_) {}
  }
  const renewalMode = user?.maintenance_renewal_mode || "reminder";

  return (
    <ClientLayout>
      <div className="mb-6">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#FF2D2D] border border-[#262626] hover:border-[#FF2D2D] px-3 py-2 transition-colors"
        >
          <House size={12} weight="bold" /> Back to home
        </Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
        <div className="lg:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Welcome back</div>
          <h1 className="font-display text-5xl md:text-6xl mt-2" data-testid="dashboard-greeting">
            Hi, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-[#a3a3a3] mt-3 max-w-xl">Here's what's in production for you right now.</p>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
          <div className="bg-[#0a0a0a] p-3 md:p-5 min-w-0">
            <FolderOpen size={18} className="text-[#FF2D2D]" />
            <div className="font-display text-xl md:text-2xl lg:text-3xl mt-2 tabular-nums truncate" data-testid="stat-active">{open.length}</div>
            <div className="text-[10px] md:text-xs text-[#a3a3a3] uppercase tracking-wider">Active</div>
          </div>
          <div className="bg-[#0a0a0a] p-3 md:p-5 min-w-0">
            <Coins size={18} className="text-[#FF2D2D]" weight="fill" />
            <div className="font-display text-xl md:text-2xl lg:text-3xl mt-2 tabular-nums truncate" data-testid="stat-points">{(user?.points_balance ?? 0).toFixed(2)}</div>
            <div className="text-[10px] md:text-xs text-[#a3a3a3] uppercase tracking-wider">Points</div>
          </div>
          <div className="bg-[#0a0a0a] p-3 md:p-5 min-w-0">
            <Clock size={18} className="text-[#FF2D2D]" />
            <div className="font-display text-xl md:text-2xl lg:text-3xl mt-2 tabular-nums truncate" data-testid="stat-balance">{formatUSD(balanceDue)}</div>
            <div className="text-[10px] md:text-xs text-[#a3a3a3] uppercase tracking-wider">Balance</div>
          </div>
        </div>
      </div>

      {/* Refer-a-friend card — sits prominently between hero stats and other CTAs */}
      <ReferralCard />

      {/* Review CTA — only when client has something they can review */}
      {showReviewCta && (
        <div className="border border-[#FF2D2D]/40 bg-gradient-to-r from-[#FF2D2D]/10 to-transparent p-4 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="review-cta">
          <div className="flex items-center gap-3">
            <Star size={18} weight="fill" className="text-[#FF2D2D]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Help us out</div>
              <div className="text-sm">
                {reviewEligible.eligible_projects.length > 0
                  ? <>You have <strong>{reviewEligible.eligible_projects.length} delivered project{reviewEligible.eligible_projects.length === 1 ? "" : "s"}</strong> ready for a review.</>
                  : "Got a minute? Leave us a quick testimonial."}
              </div>
            </div>
          </div>
          <Link
            to="/reviews/new"
            data-testid="dashboard-leave-review"
            className="btn-primary px-4 py-2 text-xs font-bold uppercase tracking-wider"
          >
            Leave a review →
          </Link>
        </div>
      )}

      {/* Pending custom charges banner — high visibility at top */}
      {charges.length > 0 && (
        <div className="border border-[#FF2D2D] bg-[#FF2D2D]/10 p-5 mb-6" data-testid="pending-charges-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <Receipt size={22} weight="fill" className="text-[#FF2D2D] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Payment requested</div>
                <div className="font-display text-2xl mt-1">
                  {charges.length === 1 ? "1 charge awaiting payment" : `${charges.length} charges awaiting payment`}
                  <span className="text-[#a3a3a3] text-base ml-2">· {formatUSD(totalChargesDue)} total</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 divide-y divide-[#FF2D2D]/20" data-testid="pending-charges-list">
            {charges.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{c.description}</div>
                  <div className="text-xs text-[#a3a3a3]">{new Date(c.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-display text-xl tabular-nums">{formatUSD(c.amount)}</span>
                  <Link
                    to={`/charges/${c.id}`}
                    data-testid={`pay-charge-${c.id}`}
                    className="btn-primary px-4 py-2 text-xs font-bold uppercase tracking-wider"
                  >
                    Pay now →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Maintenance plan badge — visible only when active */}
      {user?.has_maintenance_plan && (
        <div className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-4 mb-6" data-testid="maintenance-banner">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Star size={20} weight="fill" className="text-[#FF2D2D]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Active membership</div>
                <div className="text-sm">You have an active <strong>Maintenance Plan</strong> — website updates are free.</div>
                {maintenanceDaysLeft !== null && (
                  <div className="text-xs text-[#a3a3a3] mt-1 flex items-center gap-1.5" data-testid="maintenance-expiry">
                    <Calendar size={11} />
                    {maintenanceDaysLeft > 0
                      ? <>Renews in <strong className={maintenanceDaysLeft <= 30 ? "text-[#FF2D2D]" : "text-white"}>{maintenanceDaysLeft} days</strong> · {new Date(user.maintenance_expires_at).toLocaleDateString()}</>
                      : <>Expires today · {new Date(user.maintenance_expires_at).toLocaleDateString()}</>
                    }
                  </div>
                )}
              </div>
            </div>
            <Link
              to="/request/new?service=web_update"
              data-testid="request-website-update"
              className="btn-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
            >
              Request website update →
            </Link>
          </div>
          {/* Renewal mode toggle */}
          <div className="mt-4 pt-4 border-t border-[#FF2D2D]/20 flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">Renewal preference</span>
            <div className="flex gap-1">
              {[
                { id: "auto", label: "Auto-renew" },
                { id: "reminder", label: "Remind me" },
                { id: "manual", label: "No reminders" },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setRenewalMode(m.id)}
                  disabled={renewalSaving || renewalMode === m.id}
                  data-testid={`renewal-mode-${m.id}`}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-wider border transition-colors ${
                    renewalMode === m.id
                      ? "border-[#FF2D2D] bg-[#FF2D2D] text-white"
                      : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
        <Link
          to="/request/new"
          data-testid="quick-action-new-request"
          className="border border-[#FF2D2D] bg-[#FF2D2D] text-white p-6 flex items-center justify-between hover:bg-[#FF4D4D] transition-colors"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">Action</div>
            <div className="font-display text-3xl mt-1">Submit a new vision</div>
          </div>
          <Plus size={32} weight="bold" />
        </Link>
        <Link
          to="/points"
          data-testid="quick-action-buy-points"
          className="border border-[#262626] bg-[#0a0a0a] p-6 flex items-center justify-between hover:border-[#FF2D2D] transition-colors"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Top up</div>
            <div className="font-display text-3xl mt-1 text-white">Buy more points</div>
          </div>
          <Coins size={32} className="text-[#FF2D2D]" weight="fill" />
        </Link>
      </div>

      {/* Projects table */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl uppercase">Your projects</h2>
          <span className="text-xs text-[#a3a3a3]">{projects.length} total</span>
        </div>

        {loading ? (
          <div className="text-[#a3a3a3] p-12 text-center">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="border border-[#262626] bg-[#0a0a0a] p-12 text-center" data-testid="empty-projects">
            <div className="text-[#a3a3a3] mb-4">No projects yet. Let's start one.</div>
            <Link to="/request/new" className="btn-primary px-6 py-3 text-sm uppercase tracking-wider inline-flex items-center gap-2">
              New request <ArrowUpRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="border border-[#262626] divide-y divide-[#262626]" data-testid="projects-list">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                data-testid={`project-row-${p.id}`}
                className="grid grid-cols-12 gap-4 p-4 md:p-5 bg-[#0a0a0a] hover:bg-[#121212] transition-colors items-center"
              >
                <div className="col-span-12 md:col-span-5">
                  <div className="font-display text-xl">{p.title}</div>
                  <div className="text-xs text-[#a3a3a3] uppercase tracking-wider mt-1">{p.service_type}</div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <div className="text-xs text-[#a3a3a3]">Status</div>
                  <div className="text-sm font-semibold text-[#FF2D2D]">{STATUS_LABEL[p.status] || p.status}</div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <div className="text-xs text-[#a3a3a3]">Total</div>
                  <div className="text-sm font-semibold">
                    {p.payment_method === "points" ? `${p.total_points} pts` : formatUSD(p.total_usd)}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <div className="text-xs text-[#a3a3a3]">Payment</div>
                  <div className="text-sm font-semibold capitalize">{p.payment_status?.replace("_", " ")}</div>
                </div>
                <div className="col-span-6 md:col-span-1 flex justify-end">
                  <ArrowUpRight size={20} className="text-[#a3a3a3]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
